<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, watch } from 'vue'

defineProps<{
  administrationVisible: boolean
  cartCount: number
}>()

const route = useRoute()
const menuOpen = ref(false)
const closeButton = ref<HTMLButtonElement | null>(null)

const primaryLinks = [
  { label: 'Home', to: '/' },
  { label: 'Leistungen', to: '/#leistungen' },
  { label: 'Sortiment', to: '/shop' },
  { label: 'Referenzen', to: '/references' },
  { label: 'Über uns', to: '/about' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Blog', to: '/blog' },
]

const serviceLinks = [
  { label: 'Bühne & StageTruck', to: '/shop/category/stagetruck' },
  { label: 'Tontechnik', to: '/shop/category/tontechnik' },
  { label: 'Lichttechnik', to: '/shop/category/lichttechnik' },
  { label: 'Setangebote', to: '/shop/category/setangebote' },
]

const companyLinks = [
  { label: 'Kontakt', to: '/contact' },
  { label: 'Newsletter', to: '/newsletter' },
]

const legalLinks = [
  { label: 'Impressum', to: '/impressum' },
  { label: 'Datenschutz', to: '/privacy' },
  { label: 'AGBs', to: '/agb' },
]

function closeMenu() {
  menuOpen.value = false
}

function isPrimaryLinkActive(to: string) {
  if (to === '/') return route.path === '/' && !route.hash
  if (to === '/#leistungen') return route.path === '/' && route.hash === '#leistungen'
  if (to === '/shop') return route.path.startsWith('/shop') && route.path !== '/shop/request'
  if (to === '/references') return route.path.startsWith('/references')
  if (to === '/blog') return route.path.startsWith('/blog')
  return route.path === to
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') closeMenu()
}

watch(() => route.fullPath, closeMenu)
watch(menuOpen, async (open) => {
  document.documentElement.style.overflow = open ? 'hidden' : ''
  if (open) {
    await nextTick()
    closeButton.value?.focus()
  }
})

onMounted(() => window.addEventListener('keydown', handleEscape))
onUnmounted(() => {
  window.removeEventListener('keydown', handleEscape)
  document.documentElement.style.overflow = ''
})
</script>

<template>
  <div>
    <button
      type="button"
      class="menu-trigger"
      :aria-expanded="menuOpen"
      aria-controls="mobile-main-menu"
      aria-label="Hauptmenü öffnen"
      @click="menuOpen = true"
    >
      <i class="pi pi-bars" aria-hidden="true"></i>
    </button>

    <Teleport to="body">
      <Transition name="menu-fade">
        <div v-if="menuOpen" class="menu-layer">
          <button type="button" class="menu-backdrop" aria-label="Menü schließen" @click="closeMenu"></button>
          <aside id="mobile-main-menu" class="menu-drawer" role="dialog" aria-modal="true" aria-label="Hauptnavigation">
            <div class="menu-drawer__header">
              <NuxtLink to="/" aria-label="Mody Veranstaltungstechnik – Startseite" @click="closeMenu">
                <NuxtImg src="/rgb_negativ.png" alt="Mody Veranstaltungstechnik" class="drawer-logo" />
              </NuxtLink>
              <button ref="closeButton" type="button" class="menu-close" aria-label="Menü schließen" @click="closeMenu">
                <i class="pi pi-times" aria-hidden="true"></i>
              </button>
            </div>

            <nav class="drawer-navigation" aria-label="Mobile Hauptnavigation">
              <NuxtLink
                v-for="item in primaryLinks"
                :key="item.to"
                :to="item.to"
                active-class=""
                exact-active-class=""
                :class="{ 'is-active': isPrimaryLinkActive(item.to) }"
                @click="closeMenu"
              >
                {{ item.label }}
              </NuxtLink>
            </nav>

            <div class="drawer-section">
              <p>Direkt zu</p>
              <div class="drawer-link-grid">
                <NuxtLink v-for="item in serviceLinks" :key="item.to" :to="item.to" @click="closeMenu">{{ item.label }}</NuxtLink>
              </div>
            </div>

            <div class="drawer-meta-sections">
              <div class="drawer-section drawer-section--company">
                <p>Unternehmen</p>
                <NuxtLink v-for="item in companyLinks" :key="item.to" :to="item.to" @click="closeMenu">{{ item.label }}</NuxtLink>
                <NuxtLink v-if="administrationVisible" to="/admin/adminProducts" @click="closeMenu">Administration</NuxtLink>
              </div>
              <div class="drawer-section drawer-section--company">
                <p>Rechtliches</p>
                <NuxtLink v-for="item in legalLinks" :key="item.to" :to="item.to" @click="closeMenu">{{ item.label }}</NuxtLink>
              </div>
            </div>

            <NuxtLink to="/shop/request" class="drawer-request" @click="closeMenu">
              Angebot anfordern
              <span v-if="cartCount" class="drawer-cart-count">{{ cartCount }}</span>
            </NuxtLink>

            <div class="drawer-contact">
              <a href="tel:+436642247433"><i class="pi pi-phone"></i><span>+43 664 22 474 33</span></a>
              <a href="mailto:info@mody.at"><i class="pi pi-envelope"></i><span>info@mody.at</span></a>
              <a href="https://wa.me/436642247433" rel="noopener noreferrer" target="_blank"><i class="pi pi-whatsapp"></i><span>WhatsApp</span></a>
            </div>
          </aside>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.menu-trigger,
.menu-close{
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;

  i{
    font-size: 1.25rem;
  }

  &:hover,
  &:focus-visible{
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.28);
  }
}

.menu-layer{
  position: fixed;
  inset: 0;
  z-index: 2000;
}

.menu-backdrop{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  background: rgba(0, 0, 0, 0.72);
  border: 0;
  backdrop-filter: blur(5px);
}

.menu-drawer{
  position: absolute;
  top: 0;
  right: 0;
  width: min(92vw, 390px);
  height: 100%;
  padding: 1.25rem 1.25rem 2rem;
  overflow-y: auto;
  color: white;
  background: #111214;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: -30px 0 80px rgba(0, 0, 0, 0.45);

  a:focus-visible,
  button:focus-visible{
    outline: 2px solid #E39737;
    outline-offset: 3px;
  }
}

.menu-drawer__header{
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.drawer-logo{
  width: auto;
  height: 48px;
}

.drawer-navigation{
  display: grid;

  a{
    min-height: 52px;
    padding: 0.65rem 0;
    display: flex;
    align-items: center;
    color: white;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 1.3rem;
    font-weight: 650;
    text-decoration: none;

    &:hover,
    &:focus-visible,
    &.is-active{
      color: #E39737;
    }
  }
}

.drawer-section{
  margin-top: 1.75rem;

  > p{
    margin-bottom: 0.75rem;
    color: #E39737;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }
}

.drawer-link-grid{
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;

  a{
    padding: 0.6rem 0.8rem;
    color: rgb(214 211 209);
    background: #1b1c1f;
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: $public-radius-control;
    font-size: 0.88rem;
    text-decoration: none;
  }
}

.drawer-meta-sections{
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.drawer-section--company{
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.7rem;

  > p{
    margin-bottom: 0.1rem;
  }

  a{
    color: rgb(168 162 158);
    font-size: 0.9rem;
    text-decoration: none;
  }
}

.drawer-request{
  min-height: 52px;
  margin-top: 1.75rem;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  color: #111214;
  background: #E39737;
  border-radius: $public-radius-control;
  font-weight: 750;
  text-decoration: none;
}

.drawer-cart-count{
  min-width: 1.4rem;
  height: 1.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: #111214;
  border-radius: 999px;
  font-size: 0.75rem;
}

.drawer-contact{
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  display: grid;
  gap: 0.8rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  a{
    display: inline-flex;
    align-items: center;
    gap: 0.7rem;
    color: rgb(214 211 209);
    font-size: 0.9rem;
    text-decoration: none;
  }

  i{
    width: 1.25rem;
    color: #4C9CD4;
  }
}

.menu-fade-enter-active,
.menu-fade-leave-active{
  transition: opacity 180ms ease;

  .menu-drawer{
    transition: transform 220ms ease;
  }
}

.menu-fade-enter-from,
.menu-fade-leave-to{
  opacity: 0;

  .menu-drawer{
    transform: translateX(100%);
  }
}
</style>
